import { useCallback, useLayoutEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  allPasswordRequirementsMet,
  getPasswordRequirementStates,
} from "../../utils/passwordValidation";
import "./PasswordRequirementsTooltip.css";

const PANEL_MAX_WIDTH_PX = 272;
const VIEWPORT_PAD_PX = 8;

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2 1 21h22L12 2zm0 4.2 7.35 12.8H4.65L12 6.2zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        fill="currentColor"
        d="M12 10.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-1 3.5h2v6h-2v-6z"
      />
    </svg>
  );
}

function clampPanelLeft(triggerRect, panelWidth, inField) {
  let left = inField ? triggerRect.right - panelWidth : triggerRect.left;
  const maxLeft = window.innerWidth - panelWidth - VIEWPORT_PAD_PX;
  left = Math.max(VIEWPORT_PAD_PX, Math.min(left, maxLeft));
  return left;
}

/**
 * Compact password rules in a tooltip (label info icon or in-field warning).
 */
export default function PasswordRequirementsTooltip({
  password = "",
  inputId,
  title = "Password must include:",
  inField = false,
  instructor = false,
}) {
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [pinned, setPinned] = useState(false);
  const rules = getPasswordRequirementStates(password);
  const visible = open || pinned;
  const allMet = allPasswordRequirementsMet(password);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panelWidth = Math.min(PANEL_MAX_WIDTH_PX, window.innerWidth - VIEWPORT_PAD_PX * 2);
    setPanelStyle({
      top: rect.bottom + 6,
      left: clampPanelLeft(rect, panelWidth, inField),
      width: panelWidth,
    });
  }, [inField]);

  useLayoutEffect(() => {
    if (!visible) {
      setPanelStyle(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [visible, updatePanelPosition]);

  // Close pinned tooltip when clicking outside
  useLayoutEffect(() => {
    if (!pinned) return undefined;
    function handleOutside(e) {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setPinned(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [pinned]);

  const triggerClass = inField
    ? `pwd-req-tooltip__trigger pwd-req-tooltip__trigger--infield${
        instructor ? " pwd-req-tooltip__trigger--infield-info" : ""
      }${!instructor && password && !allMet ? " pwd-req-tooltip__trigger--warn" : ""}${
        !instructor && password && allMet ? " pwd-req-tooltip__trigger--ok" : ""
      }`
    : "pwd-req-tooltip__trigger";

  const panel =
    visible && panelStyle ? (
      <div
        id={tooltipId}
        role="tooltip"
        className="pwd-req-tooltip__panel pwd-req-tooltip__panel--floating"
        style={panelStyle}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <p className="pwd-req-tooltip__title">{title}</p>
        <ul className="pwd-req-tooltip__list" aria-live="polite">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className={`pwd-req-tooltip__item${rule.met ? " pwd-req-tooltip__item--met" : ""}`}
            >
              <span aria-hidden="true">{rule.met ? "✓" : "·"}</span>
              {rule.label}
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={`pwd-req-tooltip${inField ? " pwd-req-tooltip--infield" : ""}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          className={triggerClass}
          aria-label="Show password requirements"
          aria-expanded={visible}
          aria-controls={tooltipId}
          onFocus={() => setOpen(true)}
          onBlur={(e) => { if (!pinned) setOpen(false); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); setPinned((p) => !p); setOpen(false); }}
        >
          {inField && !instructor ? (
            <WarningIcon />
          ) : instructor ? (
            <InfoIcon />
          ) : (
            <span aria-hidden="true">i</span>
          )}
        </button>
      </span>
      {panel ? createPortal(panel, document.body) : null}
    </>
  );
}
